"use client";

import { useTranslations } from "next-intl";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import MuiLink from "@mui/material/Link";
import { Link } from "@/i18n/navigation";
import { footerColumns, type FooterLink } from "@/lib/brand";
import { navLabelSx, pageContainerSx } from "@/theme/tokens";

// Hover color comes from the global MuiLink override (violet, color-only).
const linkSx = { fontSize: "0.95rem" } as const;

/**
 * Sitemap-style link columns shown above the footer bar. Column headings and
 * link labels are translated; the structure comes from the footer config in
 * brand.ts. Internal routes (href starting with "/") use the locale-aware
 * next-intl Link so the active locale is preserved. Static assets in /public
 * (e.g. the price sheet PDF) are served at the root regardless of locale, so
 * they render as plain anchors opening in a new tab; "#" placeholders likewise
 * render as plain anchors until those pages exist.
 */
function FooterLinkItem({
  column,
  link,
}: {
  column: string;
  link: FooterLink;
}) {
  const t = useTranslations("footerNav");
  const label = t(`${column}.links.${link.key}`);
  const isAsset = link.href.endsWith(".pdf");
  const isInternal = link.href.startsWith("/") && !isAsset;
  return (
    <li>
      <MuiLink
        {...(isInternal
          ? { component: Link, href: link.href }
          : isAsset
            ? { href: link.href, target: "_blank", rel: "noopener" }
            : { href: link.href })}
        underline="none"
        color="text.secondary"
        sx={linkSx}
      >
        {label}
      </MuiLink>
    </li>
  );
}

export function FooterNav() {
  const t = useTranslations("footerNav");
  return (
    <Container maxWidth="lg" sx={pageContainerSx}>
      <Box
        sx={{
          display: "grid",
          gap: { xs: 5, md: 4 },
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        }}
      >
        {footerColumns.map((column) => (
          <Box key={column.key}>
            <Typography
              sx={{
                ...navLabelSx,
                color: "secondary.main",
                pb: 1.5,
                mb: 2.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              {t(`${column.key}.title`)}
            </Typography>
            <Box
              component="ul"
              sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1.5 }}
            >
              {column.links.map((link) => (
                <FooterLinkItem
                  key={link.key}
                  column={column.key}
                  link={link}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Container>
  );
}
